import { z } from 'zod';
import { isoDateTime, uuid } from './primitives';

/**
 * Connection wire contracts (Master Spec §6.3, §11, §14 connections). v1 ships the
 * fake_email connector (the permanent test double, §11.2) plus the Gmail connector
 * over real Google OAuth (offline access, WP-11). Connection records live in the
 * `connections` table (WP-11); credentials live in `connection_secrets` and NEVER
 * on this view (I6). The API returns this minimal view so the connections screen
 * can connect/test/revoke and show health + granted scopes.
 */

// `dispatch` (§11.3) is the first MCP-backed connector: a per-user PAT connection to the
// external "Dispatch" newsletter MCP server. `pitchreply` (§11.3, v1.9.1) is the second,
// mirroring it — a per-user PAT connection to the external "ehepitchreply" MCP server.
export const connectionProvider = z.enum(['fake_email', 'google', 'hubspot', 'dispatch', 'pitchreply']);
export type ConnectionProvider = z.infer<typeof connectionProvider>;

export const connectionHealth = z.enum(['ok', 'expired', 'error', 'degraded']);
export type ConnectionHealth = z.infer<typeof connectionHealth>;

/** A workspace connection as surfaced on the §14 connections screen. */
export const connectionView = z.object({
  id: uuid,
  provider: connectionProvider,
  externalAccount: z.string(),
  health: connectionHealth,
  scopes: z.array(z.string()),
  createdAt: isoDateTime,
  /**
   * #118: true for a MANAGED connection — auto-provisioned and backed by a shared,
   * server-side credential (the HubSpot Private App token) rather than a per-user
   * OAuth connect. The UI renders it as "Connected (shared)" and hides
   * connect/revoke/reconnect. Optional so pre-#118 payloads (and non-managed rows)
   * default to a normal, user-managed connection.
   */
  managed: z.boolean().optional().default(false),
  /**
   * #126: a human, token-free reason for a non-ok `health` — e.g. "HubSpot: hubspot
   * connection is missing required scope(s): crm.objects.emails.read". Set by the
   * gateway when a connection-bound tool call returns a scope/auth NormalizedError
   * (missing scope from #125, or an expired/unauthorized credential), and cleared on
   * the next successful call (recovery). Optional/absent when the connection is
   * healthy. NEVER contains a credential (I6). Surfaced on the Connections dashboard.
   */
  healthReason: z.string().optional(),
  /**
   * #72: the Google userinfo `name` cached in `metadata.displayName` at connect — the
   * agent's default sign-off identity (the name it signs email as, `actingAs.name`).
   * Surfaced on the Connections screen so the operator can confirm which name the agent
   * uses. Absent when no name was returned / the `profile` scope wasn't granted, and for
   * providers that don't carry a display name. Read-only; NEVER a credential (I6).
   */
  displayName: z.string().optional(),
  /**
   * §11.5 (v1.7): for a `google` connection, whether it granted a Google Drive scope —
   * i.e. whether `drive.*` tools can run against it. `false` for a Google account
   * connected before Drive support (they must reconnect once to grant it); absent for
   * non-google providers, where Drive does not apply. Drive tools fail closed
   * (`drive_scope_not_granted`) on a connection where this is false.
   */
  driveAuthorized: z.boolean().optional(),
});
export type ConnectionView = z.infer<typeof connectionView>;

/** POST …/connections — connect the fake_email test double (dev/E2E path). */
export const connectRequest = z.object({
  provider: z.literal('fake_email'),
  externalAccount: z.string().trim().min(1).max(200).default('inbox@fake.dev'),
});
export type ConnectRequest = z.infer<typeof connectRequest>;

/**
 * POST …/connections/google/start — begin the Google OAuth (offline access) flow
 * (§11.2, WP-11). The server returns an `authorizeUrl` the browser redirects to,
 * plus the opaque `state` it must echo back on the callback (CSRF binding). The
 * client sends nothing but its auth; the redirect target is server-configured.
 */
export const googleConnectStartResult = z.object({
  authorizeUrl: z.string().url(),
  state: z.string().min(1),
});
export type GoogleConnectStartResult = z.infer<typeof googleConnectStartResult>;

/**
 * GET/POST …/connections/google/callback — Google redirects back with `code` +
 * `state`. The server exchanges the code for offline tokens (behind the injectable
 * `GoogleAuthClient`), stores them via the secrets vault, and creates/updates the
 * `connections` row. Wire shape kept minimal — the tokens NEVER cross this boundary.
 */
export const googleConnectCallback = z.object({
  code: z.string().min(1),
  state: z.string().min(1),
});
export type GoogleConnectCallback = z.infer<typeof googleConnectCallback>;

/**
 * GET …/connections/google/status — whether the Google connect flow is available
 * in this deployment (issue #53). The web reads it to render the Google button as
 * enabled or disabled ("not configured") WITHOUT provoking a 503 on click. `true`
 * only when the server has GOOGLE_CLIENT_ID/SECRET/REDIRECT_URI set.
 */
export const googleConnectStatus = z.object({
  configured: z.boolean(),
});
export type GoogleConnectStatus = z.infer<typeof googleConnectStatus>;

/**
 * POST …/connections/hubspot/start — begin the HubSpot OAuth connect flow (WP-R6).
 * Mirrors the Google flow exactly: the server returns an `authorizeUrl` the browser
 * redirects to, plus the opaque CSRF `state` it must echo back on the callback. The
 * tokens NEVER cross this boundary.
 */
export const hubspotConnectStartResult = z.object({
  authorizeUrl: z.string().url(),
  state: z.string().min(1),
});
export type HubspotConnectStartResult = z.infer<typeof hubspotConnectStartResult>;

/**
 * GET/POST …/connections/hubspot/callback — HubSpot redirects back with `code` +
 * `state`. The server exchanges the code for tokens (behind the injectable
 * `HubspotAuthClient`), stores them via the secrets vault (I6), and creates the
 * `connections` row. Wire shape kept minimal — tokens NEVER cross this boundary.
 */
export const hubspotConnectCallback = z.object({
  code: z.string().min(1),
  state: z.string().min(1),
});
export type HubspotConnectCallback = z.infer<typeof hubspotConnectCallback>;

/**
 * GET …/connections/hubspot/status — whether the HubSpot connect flow is available
 * in this deployment (mirrors Google's #53 status). The web reads it to render the
 * HubSpot button enabled or disabled ("not configured") WITHOUT provoking a 503 on
 * click. `true` only when the server has HUBSPOT_CLIENT_ID/SECRET/REDIRECT_URI set.
 */
export const hubspotConnectStatus = z.object({
  configured: z.boolean(),
});
export type HubspotConnectStatus = z.infer<typeof hubspotConnectStatus>;

/**
 * POST …/connections/dispatch — connect the external "Dispatch" newsletter MCP server
 * (§11.3). Unlike Google/HubSpot this is NOT OAuth: the owner pastes a Personal Access
 * Token (scopes read/write/dispatch) from their Dispatch account. The server validates
 * it live against the MCP endpoint, then stores it ENCRYPTED (I6) — the token crosses
 * this boundary exactly ONCE, on connect, and is never returned.
 */
export const dispatchConnectRequest = z.object({
  pat: z.string().trim().min(1, 'A Dispatch token is required').max(4096),
});
export type DispatchConnectRequest = z.infer<typeof dispatchConnectRequest>;

/**
 * GET …/connections/dispatch/status — whether the Dispatch connect flow is available
 * (mirrors Google's #53 status). `true` only when the deployment can encrypt the pasted
 * PAT (an AES key is set). The web reads it to enable/disable the connect form.
 */
export const dispatchConnectStatus = z.object({
  configured: z.boolean(),
});
export type DispatchConnectStatus = z.infer<typeof dispatchConnectStatus>;

/**
 * POST …/connections/pitchreply — connect the external "ehepitchreply" MCP server
 * (§11.3, v1.9.1 — the SECOND MCP connector, mirroring `dispatch`). Not OAuth: the owner
 * pastes a Personal Access Token from their PitchReply account. The server validates it
 * live against the MCP endpoint, then stores it ENCRYPTED (I6) — the token crosses this
 * boundary exactly ONCE, on connect, and is never returned.
 */
export const pitchreplyConnectRequest = z.object({
  pat: z.string().trim().min(1, 'A PitchReply token is required').max(4096),
});
export type PitchreplyConnectRequest = z.infer<typeof pitchreplyConnectRequest>;

/**
 * GET …/connections/pitchreply/status — whether the PitchReply connect flow is available
 * (mirrors dispatch's status). `true` only when the deployment can encrypt the pasted PAT
 * (an AES key is set). The web reads it to enable/disable the connect form.
 */
export const pitchreplyConnectStatus = z.object({
  configured: z.boolean(),
});
export type PitchreplyConnectStatus = z.infer<typeof pitchreplyConnectStatus>;

/**
 * GET …/connections/fake-email/status — whether the `fake_email` connect affordance
 * (the dev/E2E test double, §11.2) should be offered in this deployment. `enabled` is
 * `true` for local development + CI (`NODE_ENV !== 'production'`) and `false` in a
 * deployed production build. The web reads it to HIDE the "Connect fake_email (dev)"
 * button in production while keeping it for local/CI. The connector itself stays
 * registered server-side regardless — this only gates the UI affordance.
 */
export const fakeEmailConnectStatus = z.object({
  enabled: z.boolean(),
});
export type FakeEmailConnectStatus = z.infer<typeof fakeEmailConnectStatus>;

/** Result of testing a connection (§14 "test"). */
export const connectionTestResult = z.object({
  ok: z.boolean(),
  health: connectionHealth,
});
export type ConnectionTestResult = z.infer<typeof connectionTestResult>;
